// Single source of truth for the DSL op surface.
//
// The canonical scope graph (every `opMeta` op group + the separate
// `pipelineVerbs` root) and the one walk that pairs each registered
// schema with its `BaseOpMeta`. Both the LLM-facing `<ExprDslReference>`
// renderer and the `op-examples` round-trip guard consume these — neither
// re-declares the scope nor re-implements the walk.
//
// Lives here (not in `op-meta.ts`) because composing the canonical scope
// requires importing `builtins` + every `ops/*` group, and `op-meta.ts`
// is a dependency leaf those modules import from — putting it there would
// create an import cycle.

import { builtins } from './builtins.ts';
import { asOpSchema, type BaseOpMeta, collectScopes, defineScope, opMeta, type Scope } from './op-meta.ts';
import { dashboardOps } from './ops/dashboard/index.ts';
import { knowledgeGraphOps } from './ops/knowledge-graph/index.ts';
import { transformOps, transformVerbScope } from './ops/transform/index.ts';

/**
 * Canonical top-level scope: every top-level op (builtins +
 * knowledge-graph + transform-scalar + dashboard) in one section.
 * Pipeline verbs / agg-context / dashboard tiles are reached
 * transitively via `childScopes`.
 */
export const canonicalDslScope: Scope<unknown> = defineScope({
  name: 'Top-level ops',
  description: 'Callable at script top level.',
  registry: opMeta,
  schemas: [...builtins, ...knowledgeGraphOps, ...transformOps, ...dashboardOps],
});

/**
 * The full set of roots the DSL surface is walked from: the `opMeta`
 * canonical root plus `transformVerbScope` (it lives in the
 * `pipelineVerbs` registry, unreachable from an `opMeta` root via
 * `childScopes`, so it is added as an explicit extra root).
 */
export const canonicalDslRoots: readonly Scope<unknown>[] = [canonicalDslScope, transformVerbScope];

export type OpMetaEntry = { readonly op: ReturnType<typeof asOpSchema>; readonly meta: BaseOpMeta | undefined };
export type ScopeOps = { readonly scope: Scope<unknown>; readonly entries: readonly OpMetaEntry[] };

/**
 * Collect every scope transitively reachable from `roots` (deduped by
 * scope identity, deterministic order via the shared `collectScopes`
 * DFS) and pair each registered schema with its `BaseOpMeta`. The
 * single home for the `as BaseOpMeta` registry-read cast.
 *
 * Cross-scope op de-duplication *by name* is intentionally NOT done
 * here — the example round-trip guard needs every op's example, while
 * the doc renderer collapses by name. Name-dedup is the consumer's
 * concern; this walk only dedups scopes by identity.
 */
export const walkOpMeta = (roots: readonly Scope<unknown>[] = canonicalDslRoots): readonly ScopeOps[] => {
  const walked = roots.flatMap((root) => collectScopes(root));
  const scopes = walked.filter((scope, i) => walked.indexOf(scope) === i);
  return scopes.map((scope) => ({
    scope,
    entries: scope.schemas.map((sch) => {
      const op = asOpSchema(sch);
      return { op, meta: scope.registry.get(op) as BaseOpMeta | undefined };
    }),
  }));
};
